import React from 'react';
import FlippableListingCard from '../Profile/FlippableListingCard';
import { useListing } from './contexts/ListingContext';

const ListingPreview = ({ 
  group = {}, 
  facilities = [], 
  currentFacilityIndex = 0, 
  currentListingIndex = 0
}) => {
  // Use the ListingContext to get the most up-to-date listing data
  const { listingsByFacilityId, getListingsForFacility } = useListing();
  
  // Log debug information
  console.log('[ListingPreview] Rendering with:', {
    facilitiesCount: facilities?.length,
    currentFacilityIndex,
    currentListingIndex,
    listingsByFacilityIdKeys: Object.keys(listingsByFacilityId || {})
  });
  // Return early if we don't have any facilities or listings
  if (!facilities || facilities.length === 0) {
    return (
      <div className="preview-section">
        <div className="preview-section-inner">
          <div className="preview-title">
            Listing Preview
          </div>
          <div className="preview-description">
            Add facilities and listings to see a preview of how they will appear to CRNAs.
          </div>
          <div className="preview-card text-center p-4 bg-light rounded">
            <p className="mb-0 text-muted">No listings to preview</p>
          </div>
        </div>
      </div>
    );
  }
  
  const facilityIndex = Math.min(currentFacilityIndex, facilities.length - 1);
  const facility = facilities[facilityIndex];
  
  // Return early if the selected facility doesn't exist
  if (!facility) {
    return (
      <div className="preview-section">
        <div className="preview-section-inner">
          <div className="preview-title">
            Listing Preview
          </div>
          <div className="preview-description">
            Add listings to your facilities to see a preview.
          </div>
          <div className="preview-card text-center p-4 bg-light rounded">
            <p className="mb-0 text-muted">No listings to preview</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Get facility ID to retrieve the most up-to-date listings from the context
  const facilityId = facility.id || facility._id;
  
  // CRITICAL: Use getListingsForFacility to get the latest listings with all edits
  // instead of directly using facility.listings which might be stale
  const listings = facilityId ? getListingsForFacility(facilityId) : (facility.listings || []);
  
  console.log(`[ListingPreview] Facility ID: ${facilityId}, Retrieved ${listings.length} listings`);
  if (listings.length > 0 && currentListingIndex < listings.length) {
    console.log(`[ListingPreview] Current listing:`, listings[currentListingIndex]);
    console.log(`[ListingPreview] Sign-on bonus:`, listings[currentListingIndex]?.compensation?.signOnBonusOffered);
  }
  
  // Return early if the facility doesn't have any listings
  if (listings.length === 0) {
    return (
      <div className="preview-section">
        <div className="preview-section-inner">
          <div className="preview-title">
            Listing Preview
          </div>
          <div className="preview-description">
            Add listings to {facility.name || 'this facility'} to see a preview.
          </div>
          <div className="preview-card text-center p-4 bg-light rounded">
            <p className="mb-0 text-muted">No listings to preview</p>
          </div>
        </div>
      </div>
    );
  }
  
  const listingIndex = Math.min(currentListingIndex, listings.length - 1);
  
  const listing = listings[listingIndex];
  const totalListings = listings.length;

  // Transform the data to match the FlippableListingCard format
  const profile = {
    // Basic info
    facilityName: facility.name || 'Facility Name',
    location: facility.location || 'Location TBD',
    groupName: group.name || 'Group Name',
    
    // Facility details
    facilityType: facility.facilityDetails?.facilityType || '',
    
    // Pay and contract
    payAmount: listing.compensation?.amount || listing.payAmount || 80,
    payPeriod: listing.compensation?.period || listing.payPeriod || 'hourly',
    contractType: listing.compensation?.contractType || listing.contractType || 'W2',
    // Debug contract type
    _debug_contractType: {
      fromCompensation: listing.compensation?.contractType,
      fromLegacy: listing.contractType,
      raw: JSON.stringify(listing.compensation)
    },
    
    // Schedule - support both flat and nested structures with correct field names
    scheduleType: listing.scheduleType || listing.schedule?.pattern || '',
    estimatedWeeklyHours: listing.estimatedWeeklyHours || listing.schedule?.estimatedWeeklyHours || 0,
    
    // Debug schedule info
    _debug_schedule: {
      fromFlatType: listing.scheduleType,
      fromNestedPattern: listing.schedule?.pattern,
      fromFlatHours: listing.estimatedWeeklyHours,
      fromNestedHours: listing.schedule?.estimatedWeeklyHours,
      raw: JSON.stringify(listing.schedule)
    },
    
    // Assignment
    assignmentType: Array.isArray(listing.assignment?.types) ? listing.assignment.types : 
                   (listing.assignment?.type ? [listing.assignment.type] : []),
    startDate: listing.assignment?.startDate || '',
    
    // Debug assignment type
    _debug_assignment: {
      fromAssignmentTypes: listing.assignment?.types,
      fromAssignmentType: listing.assignment?.type,
      raw: JSON.stringify(listing.assignment)
    },
    
    // Clinical - ensure requiredCaseTypes is always an array with multiple fallbacks
    // Check all possible locations for case types and use the first available array
    clinicalExpectations: {
      requiredCaseTypes: Array.isArray(listing._requiredCaseTypes) ? listing._requiredCaseTypes :
                         Array.isArray(listing.clinical?.requiredCaseTypes) ? listing.clinical.requiredCaseTypes :
                         Array.isArray(listing.clinicalRequirements?.requiredCaseTypes) ? listing.clinicalRequirements.requiredCaseTypes :
                         [],
      emrSystem: facility.clinicalExpectations?.emrSystem || ''
    },
    
    // Debug clinical requirements - comprehensive view of all possible data sources
    _debug_clinical: {
      fromFlattenedCache: listing._requiredCaseTypes,
      fromClinicalDTO: listing.clinical?.requiredCaseTypes,
      fromClinicialRequirements: listing.clinicalRequirements?.requiredCaseTypes,
      isFlattenedArray: Array.isArray(listing._requiredCaseTypes),
      isClinicalDTOArray: Array.isArray(listing.clinical?.requiredCaseTypes),
      isRequirementsArray: Array.isArray(listing.clinicalRequirements?.requiredCaseTypes),
      finalValue: Array.isArray(listing._requiredCaseTypes) ? listing._requiredCaseTypes :
                 Array.isArray(listing.clinical?.requiredCaseTypes) ? listing.clinical.requiredCaseTypes :
                 Array.isArray(listing.clinicalRequirements?.requiredCaseTypes) ? listing.clinicalRequirements.requiredCaseTypes :
                 [],
      rawClinical: JSON.stringify(listing.clinical),
      rawRequirements: JSON.stringify(listing.clinicalRequirements)
    },
    
    // Perks - check both flat and compensation structure
    includesHousing: listing.includesHousing || listing.compensation?.includesHousing || false,
    includesTravel: listing.includesTravel || listing.compensation?.includesTravel || false,
    malpracticeCovered: (listing.malpracticeCovered !== false) || (listing.compensation?.malpracticeCovered !== false),
    
    // Debug perks
    _debug_perks: {
      fromFlatHousing: listing.includesHousing,
      fromCompHousing: listing.compensation?.includesHousing,
      fromFlatTravel: listing.includesTravel,
      fromCompTravel: listing.compensation?.includesTravel,
      fromFlatMalpractice: listing.malpracticeCovered,
      fromCompMalpractice: listing.compensation?.malpracticeCovered
    },
    
    // Sign-on bonus - fixed structure to display correctly
    signOnBonus: {
      offered: Boolean(listing.compensation?.signOnBonusOffered || listing.signOnBonus?.offered),
      amount: listing.compensation?.signOnBonusAmount || listing.signOnBonus?.amount || 0,
      commitment: listing.compensation?.signOnBonusCommitmentYears || listing.signOnBonus?.commitment || 0
    },
    
    // Debug sign-on bonus
    _debug_signOnBonus: {
      fromCompensation: {
        offered: listing.compensation?.signOnBonusOffered,
        amount: listing.compensation?.signOnBonusAmount,
        commitmentYears: listing.compensation?.signOnBonusCommitmentYears
      },
      fromLegacy: {
        offered: listing.signOnBonus?.offered,
        amount: listing.signOnBonus?.amount,
        commitment: listing.signOnBonus?.commitment
      }
    },
    
    // Additional schedule properties - checking both flat and nested structure
    onCall: listing.schedule?.onCall || listing.onCall || false,
    weekends: listing.schedule?.weekends || listing.weekends || false,
    
    // Debug schedule boolean fields
    _debug_schedule_booleans: {
      fromNestedOnCall: listing.schedule?.onCall,
      fromFlatOnCall: listing.onCall,
      fromNestedWeekends: listing.schedule?.weekends,
      fromFlatWeekends: listing.weekends
    },
    
    // Group logo 
    group: {
      name: group.name || '',
      logoUrl: group.logoUrl || ''
    },
    
    // Website
    website: facility.website || ''
  };

  return (
    <div className="preview-section">
      <div className="preview-section-inner">
        <div className="preview-title">
          Listing Preview
          {totalListings > 0 && (
            <div className="preview-subtitle">
              {listingIndex + 1} of {totalListings}
            </div>
          )}
        </div>
        <div className="preview-description">
          This is how your listing will appear to CRNAs.
        </div>
        
        <div className="preview-card">
          <FlippableListingCard profile={profile} />
        </div>
      </div>
    </div>
  );
};

export default ListingPreview;
